import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/** Isolated from buyer storage (`dh_buyers`, `dh_session`, etc.). */
const SELLER_KEY = "dh_seller";
const SELLER_LIST_KEY = "dh_sellers";
const MODE_KEY = "dh_journey_mode";

export const emptySellerPortfolio = {
  welcome: {},
  propertyDetails: {},
  interiorFeatures: {},
  exteriorFeatures: {},
  upgrades: {},
  conditionNotes: {},
  photos: {},
  improvements: {},
  listingReadiness: {},
  tips: {},
  pageNotes: {},
  notes: {
    forAgent: "",
    showing: "",
    disclosures: "",
  },
  portfolioSummary: {},
};

export const emptySeller = {
  id: "",
  name: "",
  email: "",
  phone: "",
  agentId: "",
  agentName: "",
  brokerName: "",
  office: "",
  status: "draft",
  portfolio: { ...emptySellerPortfolio },
};

const initialSellerState = {
  current: { ...emptySeller },
  list: [],
};

function cloneEmptySeller() {
  return JSON.parse(
    JSON.stringify({
      ...emptySeller,
      portfolio: emptySellerPortfolio,
    })
  );
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function hydrateSeller(raw) {
  if (!raw || typeof raw !== "object") return cloneEmptySeller();
  return {
    ...cloneEmptySeller(),
    ...raw,
    portfolio: {
      ...emptySellerPortfolio,
      ...(raw.portfolio || {}),
    },
  };
}

function persistSeller(seller) {
  localStorage.setItem(SELLER_KEY, JSON.stringify(seller));
  if (!seller.id) return;
  const list = readJson(SELLER_LIST_KEY, []);
  const index = list.findIndex((item) => item.id === seller.id);
  if (index >= 0) list[index] = seller;
  else list.push(seller);
  localStorage.setItem(SELLER_LIST_KEY, JSON.stringify(list));
}

export function setJourneyMode(mode) {
  if (mode === "buyer" || mode === "seller") {
    localStorage.setItem(MODE_KEY, mode);
    return;
  }
  localStorage.removeItem(MODE_KEY);
}

export function getJourneyMode() {
  return localStorage.getItem(MODE_KEY);
}

export function sellerReducer(state = initialSellerState, action) {
  switch (action.type) {
    case "SET_SELLER":
      return { ...state, current: hydrateSeller(action.payload) };
    case "SET_SELLERS":
      return { ...state, list: action.payload || [] };
    case "UPDATE_SELLER_PROFILE":
      return {
        ...state,
        current: { ...state.current, ...action.payload },
      };
    case "UPDATE_PORTFOLIO":
      return {
        ...state,
        current: {
          ...state.current,
          portfolio: {
            ...state.current.portfolio,
            ...action.payload,
          },
        },
      };
    case "RESET_SELLER":
      return { ...state, current: cloneEmptySeller() };
    default:
      return state;
  }
}

const SellerStateContext = createContext(null);

export function SellerProvider({ children }) {
  const [seller, setSeller] = useState(() => hydrateSeller(readJson(SELLER_KEY, null)));

  const updateSeller = useCallback((patch) => {
    setSeller((current) => {
      const next = hydrateSeller({
        ...current,
        ...patch,
        id: current.id || crypto.randomUUID(),
        portfolio: patch.portfolio
          ? { ...current.portfolio, ...patch.portfolio }
          : current.portfolio,
      });
      persistSeller(next);
      return next;
    });
  }, []);

  const updatePortfolio = useCallback((key, value) => {
    setSeller((current) => {
      const next = hydrateSeller({
        ...current,
        id: current.id || crypto.randomUUID(),
        portfolio: {
          ...current.portfolio,
          [key]: value,
        },
      });
      persistSeller(next);
      return next;
    });
  }, []);

  const resetSeller = useCallback(() => {
    const next = cloneEmptySeller();
    localStorage.removeItem(SELLER_KEY);
    setSeller(next);
  }, []);

  useEffect(() => {
    function sync() {
      setSeller(hydrateSeller(readJson(SELLER_KEY, null)));
    }
    window.addEventListener("dh-seller-change", sync);
    return () => window.removeEventListener("dh-seller-change", sync);
  }, []);

  const value = useMemo(
    () => ({
      seller,
      current: seller,
      updateSeller,
      updatePortfolio,
      resetSeller,
    }),
    [seller, updateSeller, updatePortfolio, resetSeller]
  );

  return <SellerStateContext.Provider value={value}>{children}</SellerStateContext.Provider>;
}

export function useSellerState() {
  const context = useContext(SellerStateContext);
  if (!context) {
    throw new Error("useSellerState must be used within SellerProvider");
  }
  return context;
}
